import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

/* [PATCH rtr-podjetje v3] IBAN-i podjetja + osvezitev iz AJPES RTR registra.
   [PATCH rtr-podjetje v4] panel pokaze tudi POTRJENE racune (tip 'ok'),
   da je razvidno, da je bil pregledan vsak racun - ne le problematicni.

   POZOR: companies.ibani je seznam NAVADNIH NIZOV (ne objektov kot partners.ibani).
   Primerjava IBAN poteka na ZADNJIH 15 STEVKAH, ker so v bazi obe obliki:
   poln 'SI56xxxxxxxxxxxxxxx' in goli 15-mestni BBAN.
   Tuji IBANi (AT/HR/DE/...) so iz RTR primerjave IZVZETI - AJPES jih ne vodi. */

const norm15 = (s: any) => { const d = String(s || '').replace(/\D/g, ''); return d.length >= 15 ? d.slice(-15) : d; };
const jeTujIban = (s: any) => { const t = String(s || '').replace(/\s/g, '').toUpperCase(); return /^[A-Z]{2}/.test(t) && !t.startsWith('SI'); };

type Predlog = { tip: 'odstrani' | 'dodaj' | 'ok'; iban: string; label: string; opozorilo: boolean; izbran: boolean };

export function IbaniUrejanje({ companyId, davcna, osveziTrigger }: { companyId: string; davcna?: string; osveziTrigger?: number }) {
  const [ibani, setIbani] = useState<string[]>([]);
  const [nov, setNov] = useState('');
  const [predlogi, setPredlogi] = useState<Predlog[]>([]);
  const [rtrTece, setRtrTece] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    api.get('/companies/' + companyId + '/ibani')
      .then((r: any) => setIbani(r.data.ibani || [])).catch(() => {});
  }, [companyId]);

  const shrani = async (seznam: string[]) => {
    try {
      const r: any = await api.put('/companies/' + companyId + '/ibani', { ibani: seznam });
      setIbani(r.data.ibani);
      toast.success('IBAN-i shranjeni');
      return true;
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); return false; }
  };

  /* osvezitev iz AJPES RTR: prazno -> samodejni vpis; polno -> pregled + predlogi */
  const preveriRtr = async () => {
    const dav = String(davcna || '').trim().replace(/^SI/i, '');
    if (!dav) { toast.error('Davcna stevilka podjetja ni vpisana.'); return; }
    setRtrTece(true);
    try {
      const rr: any = await api.get('/sifranti/rtr-iban?davcna=' + encodeURIComponent(dav));
      const seznam: any[] = Array.isArray(rr.data?.racuni) ? rr.data.racuni : [];
      const odprti = seznam.filter((x: any) => x && x.iban && x.odprt);
      if (ibani.length === 0) {
        if (odprti.length) {
          const novi = odprti.map((x: any) => String(x.iban));
          await shrani(novi);
        } else {
          toast('V AJPES registru ni odprtih racunov za to davcno.');
        }
        setPredlogi([]);
      } else {
        const rtrPo15 = new Map<string, any>();
        seznam.forEach((x: any) => { if (x && x.iban) rtrPo15.set(norm15(x.iban), x); });
        const p: Predlog[] = [];
        ibani.forEach((i: string) => {
          if (jeTujIban(i)) {
            p.push({ tip: 'odstrani', iban: i, label: 'ni v AJPES registru — tuj racun', opozorilo: true, izbran: false });
            return;
          }
          const najden = rtrPo15.get(norm15(i));
          if (najden && !najden.odprt) {
            p.push({ tip: 'odstrani', iban: i, label: 'ZAPRT' + (najden.zaprt_od ? ' ' + najden.zaprt_od : ''), opozorilo: false, izbran: true });
          } else if (!najden) {
            p.push({ tip: 'odstrani', iban: i, label: 'ni v AJPES registru — preveri', opozorilo: true, izbran: false });
          } else {
            /* [PATCH rtr-podjetje v4] racun je v registru in odprt -> potrjen */
            p.push({ tip: 'ok', iban: i, label: 'potrjen v AJPES (odprt)' + (najden.banka ? ' — ' + najden.banka : ''), opozorilo: false, izbran: false });
          }
        });
        const ima = new Set(ibani.filter((i: string) => !jeTujIban(i)).map((i: string) => norm15(i)));
        odprti.forEach((x: any) => {
          if (!ima.has(norm15(x.iban))) {
            p.push({ tip: 'dodaj', iban: String(x.iban), label: 'nov iz AJPES' + (x.banka ? ' — ' + x.banka : ''), opozorilo: false, izbran: true });
          }
        });
        /* potrjene na konec, spremembe zgoraj */
        p.sort((a, b) => (a.tip === 'ok' ? 1 : 0) - (b.tip === 'ok' ? 1 : 0));
        setPredlogi(p);
        if (!p.some((x) => x.tip !== 'ok')) toast.success('AJPES: vsi racuni so usklajeni.');
      }
    } catch { toast.error('AJPES RTR: napaka pri branju registra.'); }
    setRtrTece(false);
  };

  /* gumb "Osvezi" na Profilu podjetja poveca stevec -> sprozi RTR preveritev */
  useEffect(() => {
    if (osveziTrigger && davcna) preveriRtr();
  }, [osveziTrigger]);  // eslint-disable-line

  const prevzemiPredloge = async () => {
    /* [PATCH rtr-podjetje v4] vrstice tipa 'ok' se NE uveljavljajo */
    const izbrani = predlogi.filter((x) => x.izbran && x.tip !== 'ok');
    if (!izbrani.length) { setPredlogi([]); return; }
    const ven = new Set(izbrani.filter((x) => x.tip === 'odstrani').map((x) => norm15(x.iban)));
    let arr = ibani.filter((i: string) => !ven.has(norm15(i)));
    izbrani.filter((x) => x.tip === 'dodaj').forEach((x) => {
      if (!arr.some((i: string) => norm15(i) === norm15(x.iban))) arr = [...arr, x.iban];
    });
    await shrani(arr);
    setPredlogi([]);
  };

  const jeSprememb = predlogi.some((x) => x.tip !== 'ok');

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Bancni racuni (IBAN) — za SEPA placila</div>
        <button type="button" onClick={preveriRtr} disabled={rtrTece || !davcna}
          className="px-2 py-1 text-xs rounded-lg border border-blue-300 dark:border-blue-800 text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40">
          {rtrTece ? 'Preverjam…' : 'Preveri AJPES'}
        </button>
      </div>

      {ibani.map((i) => (
        <div key={i} className="flex items-center justify-between py-1 text-sm">
          <span className="font-mono text-gray-700 dark:text-gray-200">{i}</span>
          <button onClick={() => shrani(ibani.filter((x) => x !== i))}
            className="text-red-500 hover:underline">Odstrani</button>
        </div>
      ))}
      {ibani.length === 0 &&
        <p className="text-sm text-gray-400 py-1">Ni vpisanih IBAN-ov.</p>}

      {predlogi.length > 0 && (
        <div className="mt-3 border border-blue-300 dark:border-blue-800 rounded-xl p-3 bg-blue-50/60 dark:bg-blue-950/30">
          <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-2">
            {jeSprememb ? 'AJPES RTR — pregled racunov, izberi kaj prevzeti:' : 'AJPES RTR — vsi racuni potrjeni:'}</p>
          <div className="space-y-1.5">
            {predlogi.map((x, idx) => (
              <label key={x.tip + x.iban} className={'flex items-start gap-2 text-[11px] ' + (x.tip === 'ok' ? '' : 'cursor-pointer')}>
                {x.tip === 'ok' ? (
                  <span className="mt-0.5 w-3 text-center text-green-600 dark:text-green-400">✓</span>
                ) : (
                  <input type="checkbox" checked={x.izbran} className="mt-0.5"
                    onChange={(e) => setPredlogi((ps) => ps.map((y, j) => (j === idx ? { ...y, izbran: e.target.checked } : y)))} />
                )}
                <span className="flex-1">
                  {x.tip === 'odstrani' ? (
                    <>
                      <span className="line-through text-red-400 font-mono">{x.iban}</span>
                      <span className={x.opozorilo ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}> — {x.label}</span>
                    </>
                  ) : x.tip === 'ok' ? (
                    <>
                      <span className="font-mono text-gray-600 dark:text-gray-300">{x.iban}</span>
                      <span className="text-green-600 dark:text-green-400"> — {x.label}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-green-600 dark:text-green-400 font-mono font-medium">{x.iban}</span>
                      <span className="text-gray-500"> — {x.label}</span>
                    </>
                  )}
                </span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            {jeSprememb && (
              <button type="button" onClick={prevzemiPredloge}
                className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">Prevzemi izbrano</button>
            )}
            <button type="button" onClick={() => setPredlogi([])}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">Zapri</button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <input value={nov} onChange={(e) => setNov(e.target.value)} placeholder="SI56 ..."
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm" />
        <button onClick={() => { if (nov.trim()) { shrani([...ibani, nov]); setNov(''); } }}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold">+</button>
      </div>
    </div>
  );
}
